import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import FileUpload from 'Components/FileUpload';
import Modal, { ModalBody, ModalFooter } from 'Components/Modal';
// Material UI
import { Grid, Alert } from '@mui/material';
// Components
import { Button, Form, Select } from 'Components';
import { useFileUpload } from 'Core/hooks';
import { NotificationManager } from 'Components/Notifications';
import { HasPermission } from 'Core/security';


// Actions
import {
    importUnits,
    openImportUnitsModal,
    resetImportStateUnit,
    clearErrorUnit,
    setFileKeyUnit,
    setDelimiterUnit
} from './redux/slice';

const ModalImportUnits = () => {
    const { messages } = useIntl();
    
    const delimiters = [
        { id: ',', name: messages['units_comma'] || 'Comma (,)' },
        { id: ';', name: messages['units_semicolon'] || 'Semicolon (;)' }
    ];
    
    
    const { 
        openImport, 
        loading, 
        importResponse, 
        error 
    } = useSelector((state) => state.unitsImport);
    const user = useSelector(state => state.securityRedux.user);
    
    const dispatch = useDispatch();
    const formRef = useRef();
    const filesUpload = useFileUpload([]);
    
    const [delimiter, setDelimiterLocal] = useState({
        id: '',
        name: messages['units_selectDelimiter'] || 'Select'
    });
    const [disableButton, setDisableButton] = useState(false);
    const [errorMessage, setErrorMessage] = useState({
        message: '',
        type: ''
    });

    const handleClose = useCallback(() => {
        dispatch(openImportUnitsModal(false));
        dispatch(resetImportStateUnit());
        setErrorMessage({
            message: '',
            type: ''
        });
        setDisableButton(false);
        setDelimiterLocal({
            id: '',
            name: messages['units_selectDelimiter'] || 'Select'
        });
    }, [dispatch, messages]);

    useEffect(() => {
        if (importResponse && openImport) {
            setDisableButton(true);
            NotificationManager.success(
                messages['settingsDriver_error_started'] || 'Import started successfully',
                messages['units_import_title'] || 'Units Import',
                3000,
                null,
                null,
                'success'
            );
            handleClose();
        }
    }, [importResponse, openImport, messages, handleClose]);

    useEffect(() => {
        if (error) {
            setErrorMessage({
                message: error,
                type: 'error'
            });
            setDisableButton(false);
        }
    }, [error]);

    const handleDelimiterChange = useCallback((selectedDelimiter) => {
        setDelimiterLocal(selectedDelimiter);
        dispatch(setDelimiterUnit(selectedDelimiter.id));
    }, [dispatch]);

    const handleImportSubmit = useCallback(() => {
        if (filesUpload.value.length < 1) {
            setErrorMessage({
                message: messages['settingsDriver_error_required'] || 'File is required',
                type: 'error'
            });
            return false;
        }

        if (!delimiter.id) {
            setErrorMessage({
                message: messages['units_error_delimiter'] || 'Delimiter is required',
                type: 'error'
            });
            return false;
        }

        dispatch(clearErrorUnit());
        setErrorMessage({ message: '', type: '' });

        dispatch(setFileKeyUnit(filesUpload.value[0].fileKey));
        dispatch(importUnits({ 
            fileKey: filesUpload.value[0].fileKey, 
            delimiter: delimiter.id 
        }));
    }, [filesUpload.value, delimiter.id, dispatch, messages]);

    const downloadTemplate = useCallback((e) => {
        e.preventDefault();
        
        const delimiterSelected = delimiter?.id || ',';
        
        // Base CSV headers
        let csvHeaders = [
            'ID',
            messages['units_template_unitColumn'],
            messages['units_template_vinColumn'],
            messages['units_template_makeColumn'],
            messages['units_template_modelColumn'] || 'Model',
            messages['units_template_yearColumn'] || 'Year',
            messages['units_template_tagsColumn'],
            messages['units_template_driverColumn'],
            messages['units_template_defaultDriverColumn'],
            messages['units_template_engineHoursColumn'],
            messages['units_template_odometerColumn'],
            messages['units_template_maintenance_companyColumn'],
            messages['units_template_registration_countryColumn'],
            messages['units_template_registration_stateColumn'],
            messages['units_template_registration_expiration_dateColumn'],
            messages['units_template_engine_serial_numberColumn'],
            messages['units_template_license_plateColumn'],
            messages['units_template_asset_typeColumn'],
            messages['units_template_asset_subtypeColumn']
        ];
        
        // Add conditional headers based on permissions
        if (HasPermission(user, 'evir', [295])) {
            csvHeaders.push(
                messages['units_template_inspection_device_typeColumn'],
                messages['units_template_inspection_tag_idColumn']
            );
        }
        
        if (HasPermission(user, 'zlogs', [294])) {
            csvHeaders.push(messages['units_template_power_unit_numberColumn']);
        }
        
        const csvContent = csvHeaders.join(delimiterSelected) + '\n';
        
        const blob = new Blob([csvContent], {
            type: 'text/csv;encoding:utf-8;charset=UTF-8',
        });
        
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.setAttribute('download', 'units_template.csv');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }, [delimiter, messages, user]);

    return (
        <Fragment>
            <Modal
                title={messages['units_import_title'] || 'Import Units'}
                open={openImport}
                loading={loading}
                position="right"
                size="small"
                handleClose={handleClose}
            >
                <ModalBody>
                    <div style={{ padding: '18px 24px 24px 24px', height: 650 }}>
                        {errorMessage.message && (
                            <Alert 
                                severity={errorMessage.type} 
                                onClose={() => setErrorMessage({ message: '', type: '' })}
                            >
                                {(() => {
                                    const errorCode = errorMessage.message.includes('_') 
                                        ? errorMessage.message.split('_')[0] 
                                        : null;
                                    
                                    return errorCode && messages[`units_import_error_${errorCode}`]
                                        ? messages[`units_import_error_${errorCode}`]
                                        : errorMessage.message;
                                })()}
                            </Alert>
                        )}
                        
                        <Form
                            ref={formRef}
                            onValidSubmit={handleImportSubmit}
                        >
                            <div style={{ padding: 24 }}>
                                <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
                                    <Grid item sm={12}>
                                        <div style={{ marginBottom: 16, backgroundColor: '#fff3cd', padding: 16, borderRadius: 4, fontWeight: 500 }}>
                                            {messages['units_import_instructions']}
                                        </div>
                                        <Button
                                            type="button"
                                            className="btn-blue"
                                            style={{ width: '100%', marginBottom: 16 }}
                                            onClick={downloadTemplate}
                                        >
                                            {messages['units_download_template'] || 'Download Template'}
                                        </Button>
                                    </Grid>
                                    
                                    <Grid item sm={12}>
                                        <Select
                                            id="delimiter"
                                            label={messages['units_delimiter'] || 'Delimiter'}
                                            placeholder={messages['units_selectDelimiter'] || 'Select'}
                                            valueField="id"
                                            textField="name"
                                            options={delimiters}
                                            value={delimiter}
                                            required
                                            onChange={handleDelimiterChange}
                                        />
                                    </Grid>
                                    
                                    <Grid item sm={12}>
                                        <FileUpload
                                            {...filesUpload}
                                            label={`${messages['settingsDriver_file'] || 'File'} (CSV)`}
                                            placeholder={messages['mapLandmarkImport_chooseFile'] || 'Choose file'}
                                            types=".csv"
                                            helpText={messages['mapLandmarkImport_formatSupport'] || 'Only CSV format supported'}
                                            maxSize={5}
                                            disabled={filesUpload?.value?.length > 0 || !delimiter?.id}
                                            maxSizeMessage={`${messages['max'] || 'Max'}: 5MB`}
                                        />
                                        <small className="form-text text-muted">
                                            {messages['mapLandmarkImport_maxSizeFile'] || 'Maximum file size: 5MB'}
                                        </small>
                                    </Grid>
                                </Grid>
                            </div>
                        </Form>
                    </div>
                </ModalBody>
                
                <ModalFooter style={{ bottom: 50, textAlign: 'right' }}>
                    <Button
                        onClick={handleClose}
                        disabled={disableButton}
                        className="btn-cancel"
                    >
                        {messages['units_import_cancel'] || 'Cancel'}
                    </Button>

                    <Button
                        onClick={(e) => {
                            if (e) e.preventDefault();
                            formRef.current.submit();
                        }}
                        disabled={disableButton || filesUpload?.value?.length === 0 || !delimiter?.id}
                        className="btn-blue"
                    >
                        {messages['units_import_button'] || 'Import'}
                    </Button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
};

export default ModalImportUnits;